import React, { useEffect, useState } from "react";
import { fetchPlayersForUser } from "../../api/api";
import { FormControl, InputLabel, Select, MenuItem, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

type Player = {
  sn: string;
  location: string;
};

type PlayerSelectorProps = {
  value: string | null;
  onChange: (sn: string) => void;
  disabled?: boolean;
  userId: number | null; // <-- HIER GEÄNDERT: von string | null zu number | null
};

const PlayerSelector: React.FC<PlayerSelectorProps> = ({ userId, value, onChange, disabled }) => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (userId === null) { // Prüfen auf null, da userId jetzt number | null ist
      setPlayers([]);
      return;
    }
    setLoading(true);
    setError(null);
    fetchPlayersForUser(userId) // userId ist jetzt vom korrekten Typ (number)
      .then(setPlayers)
      .catch((err) => setError(t("playerSelector.errorLoadingPlayers")))
      .finally(() => setLoading(false));
  }, [userId, t]);

  useEffect(() => {
    if (!loading && !error && players.length === 1 && value === null) {
      onChange(players[0].sn);
    }
  }, [players, loading, error, value, onChange]);

  return (
    <FormControl fullWidth sx={{ mb: 1.5 }} disabled={disabled || userId === null}> {/* disabled-Logik angepasst */}
      <InputLabel id="player-select-label" sx={{ fontSize: '0.875rem' }}>{t("playerSelector.selectPlayer")}</InputLabel>
      {loading ? (
        <CircularProgress size={24} sx={{ m: 2 }} />
      ) : (
        <Select
          labelId="player-select-label"
          value={value || ""}
          label={t("playerSelector.selectPlayer")}
          onChange={e => onChange(e.target.value as string)}
          sx={{ fontSize: '0.875rem', '& .MuiOutlinedInput-root': { height: '48px' } }}
        >
          {players.map((player) => (
            <MenuItem key={player.sn} value={player.sn} sx={{ fontSize: '0.875rem' }}>
              {player.sn} ({player.location})
            </MenuItem>
          ))}
        </Select>
      )}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && !error && players.length === 0 && userId !== null && ( // Prüfung auf userId !== null
        <Typography color="textSecondary" sx={{ mt: 1 }}>
          {t("playerSelector.noPlayersFound")}
        </Typography>
      )}
    </FormControl>
  );
};

export default PlayerSelector;